import React, { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Typography } from '@material-ui/core';
import { Form, Formik } from 'formik';
import Auth from '@aws-amplify/auth';

import { Login } from 'Links';
import { useNotify, useGlobalClient } from 'hooks';
import storage from 'storage';
import { ClientLogo, Layouts, Loaders, ValidatedTextFieldNext } from 'components';
import { getAppImage } from 'helpers';

import { ConfirmSignUpSchema, IConfirmSignUpValues } from '../validations';
import { AuthContainerProps } from '../types';

const ConfirmSignUp: FC<AuthContainerProps> = ({ authData }) => {
  const notify = useNotify();
  const client = useGlobalClient();
  const { t } = useTranslation();

  const [working, setWorking] = useState(false);
  const [resent, setResent] = useState(false);

  async function onSubmit(values: IConfirmSignUpValues) {
    setWorking(true);

    try {
      await Auth.confirmSignUp(storage.userEmail, values.code);
      await Auth.signIn(authData.username, authData.password);
    } catch (error) {
      setWorking(false);
      notify.error({ message: error.message });
    }
  }

  async function handleResend(event: MouseEvent) {
    event.preventDefault();

    setResent(true);
    setWorking(true);

    try {
      await Auth.resendSignUp(storage.userEmail);
    } catch (error) {
      notify.error({ message: error.message });
    } finally {
      setResent(false);
      setWorking(false);
    }
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/boat.png', 500)} />;
  }

  function renderMain() {
    const initialValues: IConfirmSignUpValues = {
      code: '',
    };

    return (
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={ConfirmSignUpSchema}
      >
        <Layouts.OnboardingBox component={Form}>
          <Box textAlign="center">
            <ClientLogo client={client} />
          </Box>
          <Typography align="center" paragraph variant="h2">
            {t('auth:validateSidebarHeader')}
          </Typography>
          <Box paddingTop={3}>
            <ValidatedTextFieldNext
              enableTrim
              fullWidth
              name="code"
              type="text"
              label={t('auth:validateLabel')}
              placeholder={t('auth:validateLabel')}
            />
          </Box>
          <Box paddingTop={1}>
            <Link color="textSecondary" href="#" onClick={handleResend}>
              {resent ? t('auth:validateResent') : t('auth:validateResendCode')}
            </Link>
          </Box>
          <Box paddingTop={3}>
            <Button
              fullWidth
              type="submit"
              size="large"
              variant="contained"
              color="primary"
              disabled={working}
            >
              {working ? <Loaders.Standard height={26} /> : t('auth:validateButton')}
            </Button>
          </Box>
          <Box paddingTop={5} textAlign="center">
            <Link color="textSecondary" component={Login}>
              {t('auth:backToLogin')}
            </Link>
          </Box>
        </Layouts.OnboardingBox>
      </Formik>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default ConfirmSignUp;
